<template>
  <li
    class="selected-response-option"
    :class="{
      'selected-response-option--selected': option.uiState.selected,
      [`selected-response-option--${state}`]: !!state,
      'selected-response-option--cards': layout === 'cards',
      'selected-response-option--list': layout === 'list',
      'selected-response-option--dataset-with-image': isDatasetOptionWithImage,
      'selected-response-option--list-text': layout === 'list-text',
      'selected-response-option--disabled': disabled,
    }"
  >
    <label
      class="selected-response-option__inner-wrap"
      :for="`selected-response-option__input--${assetId}--${option.id}`"
    >
      <input
        ref="inputOption"
        :aria-label="inputAriaLabel"
        :id="`selected-response-option__input--${assetId}--${option.id}`"
        :type="isMultipleResponse ? 'checkbox' : 'radio'"
        :checked="option.inputChecked"
        class="selected-response-option__input"
        :disabled="disabled"
        :name="`selected-response-option__input--${assetId}`"
        :value="option.id"
        @change="$emit('change', $event)"
        @keyup.enter="$emit('change', $event)"
        @focus="onFocus(`selected-response-option__input--${assetId}--${option.id}`)"
      >

      <!-- In the cards layouts, the image displays at the top -->
      <div
        v-if="option.resource && option.resource.url && layout === 'cards'"
        class="selected-response-option__image-wrapper"
      >
        <img
          class="selected-response-option__image"
          :alt="option.resource.title"
          :src="option.resource.url"
        >
        <K5ZoomButton
          class="selected-response-option__zoom-button"
          @click="openZoomModal"
        />
      </div>

      <div class="selected-response-option__text-wrapper">
        <span
          class="selected-response-option__marker"
          tabindex="0"
          @keyup.enter="clickInputOption()"
        >
          {{ layout === 'list'
            ? option.marker
            : `${option.marker}.` }}
        </span>
        <div>
          <TEIViewMoreContainer
            v-if="text"
            class="selected-response-option__view-more-container"
            :allow-view-more="!isDatasetOptionWithImage"
            :max-lines="formattedMaxLines"
            v-slot="props"
            :content="text"
            :state="option.uiState.selected ? 'selected' : state"
            @view-more-clicked="openViewMoreModal"
          >
            <div
              class="selected-response-option__text"
              v-html="props.content"
            />
          </TEIViewMoreContainer>
        </div>
      </div>

      <!-- In the list layout, the image displays at the end -->
      <div
        v-if="option.resource && option.resource.url && layout === 'list'"
        class="selected-response-option__image-wrapper"
      >
        <img
          class="selected-response-option__image"
          :src="option.resource.url"
        >
        <K5ZoomButton
          class="selected-response-option__zoom-button"
          @click="openZoomModal"
        />
      </div>

      <TEIOptionStatusIcon
        v-if="option.uiState.selected || state || layout === 'list-text'"
        class="selected-response-option__status-icon"
        :status="option.uiState.selected ? 'selected' : state"
      />
    </label>
  </li>
</template>

<script>
import { mapActions } from 'pinia';
import { isNumber } from 'lodash-es';
import TEIOptionStatusIcon from '@/components/teis/TEIOptionStatusIcon.vue';
import TEIViewMoreContainer from '@/components/teis/TEIViewMoreContainer.vue';
import K5ZoomButton from '@/components/ui-elements/K5ZoomButton.vue';
import { extractHtml, extractTextFromHtml } from '@/lib/utils';
import {
  useModalStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'TEISelectedResponseOption',
  components: {
    TEIOptionStatusIcon,
    TEIViewMoreContainer,
    K5ZoomButton,
  },
  props: {
    assetId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: true,
    },
    isDatasetTei: {
      type: Boolean,
      default: false,
    },
    isMultipleResponse: {
      type: Boolean,
      default: false,
    },
    option: {
      required: true,
      type: Object,
    },
    layout: {
      type: String,
      default: 'cards', // either 'cards', 'list', 'list-text'
    },
    maxLines: {
      type: Number,
      default: null,
    },
  },
  emits: ['change'],
  computed: {
    formattedMaxLines() {
      if (this.hasMaxLines) {
        return this.maxLines;
      }

      if (this.layout === 'list-text') {
        return 1;
      }

      if (this.isDatasetOptionWithImage) {
        return 1;
      }

      if (this.isDatasetTei) {
        return 4;
      }

      return this.option.resource && this.option.resource.url
        ? 3 // text options with images get capped at 3 lines
        : 8; // text-only options get capped at 8 lines
    },
    hasMaxLines() {
      return isNumber(this.maxLines);
    },
    inputAriaLabel() {
      // Make a label like: 'B. Option text option text'
      const optionText = extractTextFromHtml(
        this.option.accessibilityText || this.text || this.option.resource?.title,
      );

      return `${this.option.marker}. ${optionText}`;
    },
    isDatasetOptionWithImage() {
      return this.isDatasetTei && !!this.option?.resource?.url;
    },
    state() {
      if (this.option.uiState.correct) {
        return 'correct';
      }

      if (this.option.uiState.incorrect) {
        return 'incorrect';
      }

      if (this.layout === 'list-text') {
        return 'empty';
      }

      return null;
    },
    text() {
      return extractHtml(this.option.text || this.option.resource?.mediaCaption);
    },
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    openViewMoreModal() {
      this[types.OPEN_MODAL]({
        type: 'TEISelectedResponseZoomModal',
        option: this.option,
        contentType: 'text',
      });
    },
    openZoomModal() {
      this[types.OPEN_MODAL]({
        type: 'TEISelectedResponseZoomModal',
        option: this.option,
        contentType: this.isDatasetOptionWithImage ? 'text-and-image' : 'image',
      });
    },
    onFocus(idElement) {
      // resolve focus issues on Windows browsers
      document.getElementById(idElement).focus();
    },
    clickInputOption() {
      // Click the input when the marker is entered
      this.$refs.inputOption.click();
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-fonts';

.selected-response-option {
  background-color: $nebula-color-white;
  border: 3px solid $nebula-color-white;
  border-radius: $nebula-border-radius-large + 3px;
  box-shadow: $nebula-shadow-100;
  color: $nebula-color-platform-neutral-1100;
  flex-basis: 25%;
  flex-grow: 1;
  flex-shrink: 1;
  list-style-type: none;
  margin: 0;
  max-width: 220px;
  min-width: 0;
  padding: 0;
  position: relative;
  transition: all $nebula-transition-default;

  .selected-response-tei__options--2-across &,
  .selected-response-tei__options--3-across & {
    max-width: 270px;
  }

  .selected-response-tei__options--5-across &,
  .selected-response-tei__options--6-across & {
    min-width: 140px;
  }

  .selected-response-tei--horizontal-with-cards & {
    max-width: 200px;
  }

  &--selected,
  &--correct,
  &--incorrect,
  &:not(.selected-response-option--disabled):hover,
  &:not(.selected-response-option--disabled):focus-within {
    &:not(.selected-response-option--list-text) {
      box-shadow: 0 5px 26px 5px rgba(0, 0, 0, 0.1); // custom shadow per design
      transform: translateY(-10px);
    }
  }

  &:not(.selected-response-option--disabled) {
    .selected-response-option__inner-wrap:hover,
    .selected-response-option__inner-wrap:focus {
      cursor: pointer;
    }

    &:not(.selected-response-option--selected) {
      &.selected-response-option--empty {
        &.selected-response-option--list-text {
          &:hover,
          &:focus-within {
            .selected-response-option__inner-wrap {
              background-color: var(--tei-sr-option-hover-color);
            }

            .selected-response-option__status-icon {
              background-color: $nebula-color-white;
              border-color: var(--tei-button-primary-color);
            }
          }
        }
      }
    }
  }

  &--selected, &--correct, &--incorrect {
    &:not(.selected-response-option--list-text) {
      border: 5px solid $nebula-color-white;
      border-radius: $nebula-border-radius-large + 5px;
    }
  }

  &--cards {
    min-width: 180px; // force options to wrap to two rows
  }

  &--list {
    max-width: 100%;
    width: 100%;
  }

  &--list-text {
    box-shadow: none;
    max-width: 100%;
    width: 100%;
  }

  &--dataset-with-image .tei-view-more-container {
    .tei-view-more-container__content {
      display: block;
    }

    .tei-view-more-container__content-inner-wrap {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &--dataset-with-image,
  &--list-text {
    .tei-view-more-container .tei-view-more-container__view-more {
      display: none;
    }
  }

  &__inner-wrap {
    border-radius: $nebula-border-radius-large;
    display: block;
    height: 100%;
    min-height: 140px;
    overflow: hidden;

    .selected-response-option--correct & {
      background-color: $nebula-color-feedback-success-100;
    }

    .selected-response-option--incorrect & {
      background-color: $nebula-color-platform-neutral-300;
    }

    .selected-response-option--selected & {
      background-color: var(--tei-card-selection-color);
    }

    .selected-response-option--selected.selected-response-option--list-text & {
      background-color: var(--tei-sr-option-list-selection-color);
    }

    .selected-response-option--list & {
      align-items: center;
      display: flex;
      gap: $nebula-space-2x;
      min-height: initial;
    }

    .selected-response-option--list-text & {
      border-radius: 12px;
      min-height: auto;
      padding-block: 2px;
      padding-inline: 44px $nebula-space-half;
    }
  }

  &__image-wrapper {
    flex-shrink: 0;
    position: relative;
  }

  &__image {
    aspect-ratio: 16 / 9;
    display: block;
    object-fit: cover;
    width: 100%;

    .selected-response-option--list & {
      height: 73px;
      margin-inline-end: $nebula-space-2x;
      width: 130px;
    }

    .selected-response-tei__options--5-across .selected-response-option--cards & {
      aspect-ratio: 51 / 35; // Match designs
    }

    .selected-response-tei__options--6-across .selected-response-option--cards & {
      aspect-ratio: 83 / 62; // Match designs
    }
  }

  &__zoom-button {
    bottom: $nebula-space-1x;
    inset-inline-end: $nebula-space-1x;
    position: absolute;

    .selected-response-option--list & {
      bottom: 4px;
      inset-inline-end: 20px;
    }
  }

  &__text-wrapper {
    padding: 12px;

    .selected-response-option--list & {
      align-items: center;
      display: flex;
      gap: $nebula-space-2x;
      padding: $nebula-space-2x;
    }

    .selected-response-option--list-text & {
      align-items: center;
      display: flex;
      gap: $nebula-space-2x;
      padding: 0;
    }

    .selected-response-tei__options--5-across .selected-response-option--cards &,
    .selected-response-tei__options--6-across .selected-response-option--cards & {
      padding: $nebula-space-1x;
    }
  }

  &__marker {
    tei-mikado-heading-secondary-bold();

    .selected-response-option--list & {
      tei-mikado-heading-primary-bold();
    }

    .selected-response-option--list-text & {
      tei-mikado-body-secondary-bold();
    }

    .selected-response-tei__options--5-across &,
    .selected-response-tei__options--6-across & {
      tei-mikado-body-secondary-bold();
    }
  }

  &__text {
    tei-mikado-body-small-regular();

    flex-grow: 1;

    .selected-response-option--list-text & {
      tei-mikado-body-secondary-regular();
    }
  }

  &__input {
    cursor: pointer;
    opacity: 0;
    position: absolute;
  }

  &__status-icon {
    position: absolute;

    &:not(.tei-option-status-icon--empty) {
      border: 4px solid $nebula-color-white; // match border from response cards
      inset-inline-end: - $nebula-space-2x;
      top: -12px;
    }

    .selected-response-option--list-text & {
      border-radius: $nebula-space-1x;
      border-width: 2px;
      height: $nebula-space-3x;
      inset-inline-start: $nebula-space-1x;
      top: 5px;
      width: $nebula-space-3x;
    }

    .selected-response-option--list-text.selected-response-option--selected \
      :not(.selected-response-option--disabled):focus-within & {
      box-shadow: var(--tei-sr-input-focus-shadow);
    }
  }
}
</style>
